import { useEffect, useRef, useState } from 'react';
import { useOpenApiClient } from './base/useOpenApiClient';
import { ErrorResponse, GetManyRequestOptions } from 'interfaces/baseTypes';
import type { components } from './base/schema';

// ProductGroup types from schema
export type ProductGroupDto = components["schemas"]["ProductGroupResponse"];
export type ProductGroupCreateRequest = components["schemas"]["ProductGroupCreateRequest"];
export type ProductGroupUpdateRequest = components["schemas"]["ProductGroupUpdateRequest"];
export type ProductGroupDtoPaginationResult = components["schemas"]["ProductGroupResponsePaginationResult"];
export type ProductGroupType = components["schemas"]["ProductGroupType"];

const useProductGroupOpenApi = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<ErrorResponse | null>(null);
    const {client, getClient} = useOpenApiClient();
    const clientRef = useRef(client);

    useEffect(() => {
        clientRef.current = client;
    }, [client]);

    async function getProductGroups(options: GetManyRequestOptions & {
        type?: ProductGroupType;
        includeInactive?: boolean;
        languageId?: number;
    } = {}): Promise<ProductGroupDto[]> {
        const { type, includeInactive } = options;
        setLoading(true);
        setErrorMessage(null);
        try {
            if(!clientRef.current){
                clientRef.current = getClient();
            }
            
            if (!clientRef.current) {
                throw new Error('API client is not initialized');
            }
            
            const response = await clientRef.current.GET('/api/product-groups/paginated', {
                params: {
                    query: {
                        page: 1,
                        pageSize: 1000,
                        type,
                        includeInactive,
                    }
                }
            });

            const error = response?.error as unknown as ErrorResponse;
            if (error) {
                setErrorMessage(error);
                return [];
            }

            const paginatedResult = response?.data as ProductGroupDtoPaginationResult;
            return paginatedResult?.data || [];
        } catch (e: any) {
            setErrorMessage(e.message || 'An error occurred');
            return [];
        } finally {
            setLoading(false);
        }
    }

    async function getProductGroupsPaginated(options: GetManyRequestOptions & {
        type?: ProductGroupType;
        includeInactive?: boolean;
        search?: string;
        languageId?: number;
    } = {}): Promise<ProductGroupDtoPaginationResult> {
        const { page = 1, pageSize = 10, type, includeInactive, search } = options;
        setLoading(true);
        setErrorMessage(null);
        try {
            if(!clientRef.current){
                clientRef.current = getClient();
            }
            
            if (!clientRef.current) {
                throw new Error('API client is not initialized');
            }
            
            const response = await clientRef.current.GET('/api/product-groups/paginated', {
                params: {
                    query: {
                        page,
                        pageSize,
                        type,
                        includeInactive,
                        search,
                    }
                }
            });

            const error = response?.error as unknown as ErrorResponse;
            if (error) {
                setErrorMessage(error);
                return { data: [], total: 0 };
            }

            return response?.data as ProductGroupDtoPaginationResult;
        } catch (e: any) {
            setErrorMessage(e.message || 'An error occurred');
            return { data: [], total: 0 };
        } finally {
            setLoading(false);
        }
    }

    async function getProductGroup(id: number, minimal?: boolean): Promise<ProductGroupDto | undefined> {
        setLoading(true);
        setErrorMessage(null);
        try {
            if(!clientRef.current){
                clientRef.current = getClient();
            }
            
            if (!clientRef.current) {
                throw new Error('API client is not initialized');
            }
            
            const response = await clientRef.current.GET('/api/product-groups/{id}', {
                params: { 
                    path: { id }
                }
            });

            const error = response?.error as unknown as ErrorResponse;
            if (error) {
                setErrorMessage(error);
                return undefined;
            }

            return response?.data as ProductGroupDto;
        } catch (e: any) {
            setErrorMessage(e.message || 'An error occurred');
            return undefined;
        } finally {
            setLoading(false);
        }
    }

    async function getProductGroupWithProducts(id: number, languageId?: number): Promise<ProductGroupDto | undefined> {
        setLoading(true);
        setErrorMessage(null);
        try {
            if(!clientRef.current){
                clientRef.current = getClient();
            }
            
            if (!clientRef.current) {
                throw new Error('API client is not initialized');
            }
            
            const response = await clientRef.current.GET('/api/product-groups/{id}/with-products', {
                params: { 
                    path: { id }
                }
            });

            const error = response?.error as unknown as ErrorResponse;
            if (error) {
                setErrorMessage(error);
                return undefined;
            }

            return response?.data as ProductGroupDto;
        } catch (e: any) {
            setErrorMessage(e.message || 'An error occurred');
            return undefined;
        } finally {
            setLoading(false);
        }
    }

    async function createProductGroup(productGroup: ProductGroupCreateRequest): Promise<ProductGroupDto | undefined> {
        setLoading(true);
        setErrorMessage(null);
        try {
            if(!clientRef.current){
                clientRef.current = getClient();
            }

            if (!clientRef.current) {
                throw new Error('API client is not initialized');
            }
            
            const response = await clientRef.current.POST('/api/product-groups', {
                body: productGroup,
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const error = response?.error as unknown as ErrorResponse;
            if (error) {
                setErrorMessage(error);
                return undefined;
            }

            return response?.data as ProductGroupDto;
        } catch (e: any) {
            setErrorMessage(e.message || 'An error occurred');
            return undefined;
        } finally {
            setLoading(false);
        }
    }

    async function updateProductGroup(id: number, productGroup: ProductGroupUpdateRequest): Promise<ProductGroupDto | undefined> {
        setLoading(true);
        setErrorMessage(null);
        try {
            if(!clientRef.current){
                clientRef.current = getClient();
            }
            
            if (!clientRef.current) {
                throw new Error('API client is not initialized');
            }

            const response = await clientRef.current.PUT('/api/product-groups/{id}', {
                params: { path: { id } },
                body: productGroup,
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const error = response?.error as unknown as ErrorResponse;
            if (error) {
                setErrorMessage(error);
                return undefined;
            }

            return response?.data as ProductGroupDto;
        } catch (e: any) {
            setErrorMessage(e.message || 'An error occurred');
            return undefined;
        } finally {
            setLoading(false);
        }
    }

    async function deleteProductGroup(id: number): Promise<void> {
        setLoading(true);
        setErrorMessage(null);
        try {
            if(!clientRef.current){
                clientRef.current = getClient();
            }
            
            if (!clientRef.current) {
                throw new Error('API client is not initialized');
            }

            const response = await clientRef.current.DELETE('/api/product-groups/{id}', {
                params: { path: { id } }
            });

            const error = response?.error as unknown as ErrorResponse;
            if (error) {
                setErrorMessage(error);
                return undefined;
            }
        } catch (e: any) {
            setErrorMessage(e.message || 'An error occurred');
            return undefined;
        } finally {
            setLoading(false);
        }
    }

    async function assignProductsBySkus(id: number, skus: string[]): Promise<ProductGroupDto | undefined> {
        setLoading(true);
        setErrorMessage(null);
        try {
            if(!clientRef.current){
                clientRef.current = getClient();
            }
            
            if (!clientRef.current) {
                throw new Error('API client is not initialized');
            }

            const response = await clientRef.current.POST('/api/product-groups/{id}/products/assign-by-skus', {
                params: { path: { id } },
                body: { skus },
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const error = response?.error as unknown as ErrorResponse;
            if (error) {
                setErrorMessage(error);
                return undefined;
            }

            return response?.data as ProductGroupDto;
        } catch (e: any) {
            setErrorMessage(e.message || 'An error occurred');
            return undefined;
        } finally {
            setLoading(false);
        }
    }

    async function assignProductsByIds(id: number, productIds: number[]): Promise<ProductGroupDto | undefined> {
        setLoading(true);
        setErrorMessage(null);
        try {
            if(!clientRef.current){
                clientRef.current = getClient();
            }
            
            if (!clientRef.current) {
                throw new Error('API client is not initialized');
            }

            const response = await clientRef.current.POST('/api/product-groups/{id}/products/assign-by-ids', {
                params: { path: { id } },
                body: { productIds },
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const error = response?.error as unknown as ErrorResponse;
            if (error) {
                setErrorMessage(error);
                return undefined;
            }

            return response?.data as ProductGroupDto;
        } catch (e: any) {
            setErrorMessage(e.message || 'An error occurred');
            return undefined;
        } finally {
            setLoading(false);
        }
    }

    async function unassignProductsBySkus(id: number, skus: string[]): Promise<ProductGroupDto | undefined> {
        setLoading(true);
        setErrorMessage(null);
        try {
            if(!clientRef.current){
                clientRef.current = getClient();
            }
            
            if (!clientRef.current) {
                throw new Error('API client is not initialized');
            }
            
            const response = await clientRef.current.DELETE('/api/product-groups/{id}/products/remove-by-skus', {
                params: { path: { id } },
                body: { skus },
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const error = response?.error as unknown as ErrorResponse;
            if (error) {
                setErrorMessage(error);
                return undefined;
            }

            return response?.data as ProductGroupDto;
        } catch (e: any) {
            setErrorMessage(e.message || 'An error occurred');
            return undefined;
        } finally {
            setLoading(false);
        }
    }

    async function unassignProductsByIds(id: number, productIds: number[]): Promise<ProductGroupDto | undefined> {
        setLoading(true);
        setErrorMessage(null);
        try {
            if(!clientRef.current){
                clientRef.current = getClient();
            }
            
            if (!clientRef.current) {
                throw new Error('API client is not initialized');
            }
            
            const response = await clientRef.current.DELETE('/api/product-groups/{id}/products/remove-by-ids', {
                params: { path: { id } },
                body: { productIds },
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const error = response?.error as unknown as ErrorResponse;
            if (error) {
                setErrorMessage(error);
                return undefined;
            }

            return response?.data as ProductGroupDto;
        } catch (e: any) {
            setErrorMessage(e.message || 'An error occurred');
            return undefined;
        } finally {
            setLoading(false);
        }
    }

    return { 
        getProductGroups,
        getProductGroupsPaginated, 
        getProductGroup,
        getProductGroupWithProducts,
        createProductGroup, 
        updateProductGroup, 
        deleteProductGroup,
        assignProductsBySkus,
        assignProductsByIds,
        unassignProductsBySkus,
        unassignProductsByIds,
        loading, 
        errorMessage 
    };
}

export default useProductGroupOpenApi;
